import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const MIN_RECOMMENDED_PHOTOS = 3;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  padding: 0.85rem 0 0.15rem;
  margin-bottom: 0.15rem;
`;

const Top = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
`;

const Title = styled.p`
  margin: 0;
  font-size: 0.95rem;
  font-weight: 650;
  color: #1c1917;
  letter-spacing: -0.01em;
  line-height: 1.35;
`;

const Count = styled.span`
  flex-shrink: 0;
  font-size: 0.78rem;
  font-weight: 650;
  color: ${(p) => (p.$ready ? '#047857' : '#78716c')};
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 0.82rem;
  color: #57534e;
  line-height: 1.45;
`;

const ProgressTrack = styled.div`
  height: 3px;
  border-radius: 999px;
  background: #e7e5e4;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${(props) => props.$pct}%;
  border-radius: inherit;
  background: ${(props) => (props.$pct >= 100 ? '#059669' : '#111827')};
  transition: width 0.2s ease;
`;

const FaceTip = styled.p`
  margin: 0;
  font-size: 0.78rem;
  color: #78716c;
  line-height: 1.4;
`;

/**
 * Compact listing photo hint — sits above the uploader without looking like a promo card.
 */
const ListingPhotosGuidance = ({
  photoCount = null,
  supportsVideo = false,
  minRecommended = MIN_RECOMMENDED_PHOTOS,
  maxMedia = null,
}) => {
  const { t } = useTranslation();
  const target = Math.max(1, Number(minRecommended) || MIN_RECOMMENDED_PHOTOS);
  const count = photoCount == null ? null : Math.max(0, Number(photoCount) || 0);
  const progressPct =
    count == null ? 0 : Math.min(100, Math.round((count / target) * 100));

  return (
    <Card>
      <Top>
        <Title>
          {supportsVideo
            ? t('listing.photoGuidanceTitleVideo')
            : t('listing.photoGuidanceTitle')}
        </Title>
        {count != null ? (
          <Count $ready={count >= target}>
            {count}/{target}
            {maxMedia != null ? ` · ${t('listing.maxFiles', { max: maxMedia })}` : ''}
          </Count>
        ) : null}
      </Top>
      <Subtitle>
        {supportsVideo
          ? t('listing.photoGuidanceSubtitleVideo')
          : t('listing.photoGuidanceSubtitle')}
      </Subtitle>
      {count != null ? (
        <ProgressTrack>
          <ProgressFill $pct={progressPct} />
        </ProgressTrack>
      ) : null}
      <FaceTip>{t('listing.photoGuidanceFaceTip')}</FaceTip>
    </Card>
  );
};

export default ListingPhotosGuidance;
export { MIN_RECOMMENDED_PHOTOS };
